import { Component, OnInit } from "@angular/core";
import { MainService } from "core/main.service";
import { ActivatedRoute } from "@angular/router";
import { AuthenticationService } from "core/authentication.service";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { assertDefined, hasProperty } from "core/functions";
import { KeyboardService } from "common/keyboard.service";
import { HttpErrorResponse } from "@angular/common/http";
import ApplicationContext = stages.context.ApplicationContext;

@Component({
	selector: "stages-start-sudo-dialog",
	templateUrl: "./sudo-dialog.component.html",
	styleUrls: ["./sudo-dialog.component.scss"],
})
export class SudoDialogComponent implements OnInit {
	workspaceId: string;
	processVersion: string;
	applicationContext?: ApplicationContext;
	sudoFormGroup: UntypedFormGroup = new UntypedFormGroup({});
	submitting = false;
	captchaImage?: string;
	captchaToken?: string;
	needsCode = false;

	constructor(
		private route: ActivatedRoute,
		private readonly mainService: MainService,
		private readonly authenticationService: AuthenticationService,
		private readonly keyboardService: KeyboardService,
	) {
		this.workspaceId = this.route.snapshot.paramMap.get("workspaceId")!;
		this.processVersion = this.route.snapshot.paramMap.get("processVersion")!;
	}

	async ngOnInit(): Promise<void> {
		this.applicationContext = await this.mainService.getApplication(
			this.route.snapshot.paramMap.get("processVersion")!,
		);
		if (!this.applicationContext.superUser && !this.applicationContext.sso) {
			this.sudoFormGroup.addControl("sudoPassword", new UntypedFormControl("", [Validators.required]));
			this.sudoFormGroup.addControl("captchaText", new UntypedFormControl(""));
			if (this.applicationContext.otpType !== null) {
				this.sudoFormGroup.addControl("code", new UntypedFormControl(""));
			}
		}
	}

	async onKeyUp(event: KeyboardEvent): Promise<void> {
		if (!this.keyboardService.isReturnKeyPressed(event)) {
			return;
		}
		return this.onSave();
	}

	async onCancel(): Promise<void> {
		await this.mainService.closePopup(this.route);
	}

	async onSave(): Promise<void> {
		if (!this.sudoFormGroup.valid) {
			return;
		}
		assertDefined(this.applicationContext);
		this.submitting = true;
		try {
			if (this.applicationContext?.superUser) {
				await this.authenticationService.quitSudo();
			} else {
				const sudoPassword = this.sudoFormGroup.get("sudoPassword")
					? this.sudoFormGroup.get("sudoPassword")!.value
					: "";
				const captchaText = this.sudoFormGroup.get("captchaText")
					? this.sudoFormGroup.get("captchaText")!.value
					: undefined;
				const securityCode = this.sudoFormGroup.get("code") ? this.sudoFormGroup.get("code")!.value : undefined;
				try {
					await this.authenticationService.startSudo(
						this.applicationContext!.username,
						sudoPassword,
						this.applicationContext!.sso,
						this.processVersion,
						captchaText,
						this.captchaToken,
						securityCode,
					);
				} catch (err: unknown) {
					if (err instanceof HttpErrorResponse && err.status === 403) {
						await this.handleSSO(sudoPassword, captchaText);
					} else {
						this.handleError(err);
						return;
					}
				}
			}
		} finally {
			this.submitting = false;
		}
		await this.closeDialogAndGoHome();
		await this.mainService.getWorkspaceView(this.workspaceId, this.processVersion);
	}

	//workaround for Kerberos/SPNEGO login
	private async handleSSO(sudoPassword: string, captchaText: string): Promise<void> {
		try {
			await delay(500);
			await this.authenticationService.startSudo(
				this.applicationContext!.username,
				sudoPassword,
				this.applicationContext!.sso,
				this.processVersion,
				captchaText,
				this.captchaToken,
			);
		} catch (err: unknown) {
			this.handleError(err);
		}
	}

	private handleError(err: unknown): void {
		if (err && hasProperty(err, "error")) {
			const error = err.error;
			if (hasProperty(error, "captchaImage") && hasProperty(error, "captchaToken") && error.captchaImage) {
				this.sudoFormGroup.get("captchaText")!.setValue("");
				this.captchaImage = error.captchaImage as string;
				this.captchaToken = error.captchaToken as string;
				const errorInputName = this.needsCode ? "code" : "sudoPassword";
				this.sudoFormGroup.get(errorInputName)!.setErrors({ accountLocked: true });
				this.sudoFormGroup.get(errorInputName)!.markAsTouched();
				return;
			}
			if (hasProperty(error, "otpType") && error.otpType !== null) {
				this.needsCode = true;
				this.sudoFormGroup.get("code")!.setErrors({ required: true });
				this.sudoFormGroup.get("code")!.markAsTouched();
				if (this.hasErrorMessage(error, "login.error.code.verify.failed")) {
					this.sudoFormGroup.get("code")!.setErrors({ incorrectCode: true });
					this.sudoFormGroup.get("code")!.markAsTouched();
					return;
				}
				return;
			}
			if (this.hasErrorMessage(error, "login.error.loginincorrect")) {
				this.sudoFormGroup.get("sudoPassword")!.setErrors({ incorrectPassword: true });
				this.sudoFormGroup.get("sudoPassword")!.markAsTouched();
				return;
			}
		}
		throw err;
	}

	private hasErrorMessage(error: unknown, errorKey: string): boolean {
		return (
			hasProperty(error, "messages") &&
			error.messages instanceof Array &&
			error.messages[0] &&
			error.messages[0].keys &&
			error.messages[0].keys[0] &&
			error.messages[0].keys[0] === errorKey
		);
	}

	private async closeDialogAndGoHome(): Promise<void> {
		await this.mainService.closePopup(this.route);
		await this.mainService.goHome(this.processVersion);
		this.mainService.closeDialog(this.route);
	}

	hasErrorAfterEditing(controlName: string, error: string): boolean {
		const control = assertDefined(this.sudoFormGroup.get(controlName));
		return control.errors != null && control.errors[error] && (control.dirty || control.touched);
	}

	resetValidity(...controlNames: string[]): void {
		for (const controlName of controlNames) {
			const control = this.sudoFormGroup.get(controlName);
			if (control && control.errors != null && Object.keys(control.errors).length > 0) {
				control.updateValueAndValidity();
			}
		}
	}

	getMsgKey(): string {
		if (this.applicationContext?.superUser) {
			return "user.sudo.quit";
		}
		return "user.sudo.start";
	}

	showCodeInput(): boolean {
		return this.needsCode;
	}
}

async function delay(ms: number): Promise<void> {
	// eslint-disable-next-line no-promise-executor-return
	return new Promise((resolve) => setTimeout(resolve, ms));
}
